import { ColorTypes } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Control, useForm } from 'react-hook-form';

import {
  AssetDefaultOrSelectedTypeMap,
  PublicOfferAssetToInvestNormalized,
  PublicOfferDetailsModalityWithTimelineNormalized,
} from '../../../../../../../store/product/types';

import { PublicOfferFormDataT1, getValidationCreateSchemaT1 } from './constants';

export const usePublicOfferAssetQuantityController = (
  _: AssetDefaultOrSelectedTypeMap['publicOffers'],
  offerDetails: PublicOfferAssetToInvestNormalized,
  modality: PublicOfferDetailsModalityWithTimelineNormalized
) => {
  const defaultQuantity = modality.minimumQuantity;
  const minimumValue = modality.minimumQuantity * offerDetails.unitaryPrice;
  const maximumValue = modality.maximumQuantity * offerDetails.unitaryPrice;

  const [isFocused, setIsFocused] = useState(false);
  const [prevValue, setPrevValue] = useState(0);
  const [showMessageAdjustUnitPrice, setShowMessageAdjustUnitPrice] = useState(false);
  const [valueApplied, setValueApplied] = useState(minimumValue);
  const [amountItem, setAmountItem] = useState(defaultQuantity);

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minValueFormatted = Utils.Mask.Money(minimumValue);

  const tableForm = useForm<PublicOfferFormDataT1>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      priceItem: minValueFormatted,
    },
    resolver: zodResolver(getValidationCreateSchemaT1(minimumValue, maximumValue)),
  });

  const isAbleToChange = (value: number) => {
    return !!(prevValue !== value && value >= minimumValue && value <= maximumValue);
  };

  const getCorrectPriceItem = () => {
    const currentValue = tableForm.getValues('priceItem');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    const multiple = Number(offerDetails.unitaryPrice.toFixed(2));
    const isMultipleBalance = currentValueUnformatted % multiple === 0;

    const quotient = parseInt((currentValueUnformatted / multiple).toFixed(0), 10);
    const multipliedValue = quotient * parseFloat(multiple.toFixed(2));
    const priceItem = parseFloat(multipliedValue.toFixed(2));

    return { priceItem, isMultipleBalance, quotient };
  };

  const getPriceItemOrMinimumApplication = () => {
    const { priceItem } = getCorrectPriceItem();
    const minimumApplication = minimumValue;
    return priceItem > minimumApplication ? priceItem : minimumApplication;
  };

  const handleSubmit = async () => {
    const priceItem = getPriceItemOrMinimumApplication();

    tableForm.setValue('priceItem', Utils.Mask.Money(priceItem));
    setValueApplied(priceItem);

    setIsFocused(false);
  };

  const handleChangeAmountItem = async (value: number) => {
    const multiple = Number(offerDetails.unitaryPrice.toFixed(2));
    const multipliedValue = value * parseFloat(multiple.toFixed(2));
    const priceItem = parseFloat(multipliedValue.toFixed(2));

    setAmountItem(value);
    tableForm.setValue('priceItem', Utils.Mask.Money(priceItem));
    setValueApplied(priceItem);
  };

  const handleInputBlur = async () => {
    const currentValue = tableForm.getValues('priceItem');

    if (currentValue !== minValueFormatted) {
      const { isMultipleBalance, quotient, priceItem } = getCorrectPriceItem();
      const priceItemFormatted = Utils.Mask.Money(priceItem);

      if (isAbleToChange(priceItem)) {
        tableForm.setValue('priceItem', priceItemFormatted);
        setValueApplied(priceItem);
        setPrevValue(priceItem);
        setAmountItem(quotient);

        if (!isMultipleBalance) {
          setShowMessageAdjustUnitPrice(true);
        }
      }
    }
    if (isFocused) {
      setIsFocused(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setShowMessageAdjustUnitPrice(false);
    setPrevValue(minimumValue);
    if (tableForm.formState.errors?.priceItem) {
      tableForm.resetField('priceItem');
    }
  };

  const handleInputClear = () => {
    setIsFocused(false);
    tableForm.resetField('priceItem');
    tableForm.clearErrors('priceItem');
  };

  const helpTextWhenIsFocused = showMessageAdjustUnitPrice
    ? 'Ajustado por preço unitário'
    : `Mínimo: ${Utils.Mask.Money(minimumValue)}`;

  const messageAdjustUnitPriceColor = {
    color: (isFocused ? 'brand.tertiary.principal' : 'neutral.light') as ColorTypes.Option,
  };

  return {
    minimumValue,
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleChangeAmountItem,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    showMessageAdjustUnitPrice,
    helpTextProps: messageAdjustUnitPriceColor,
    helpText: helpTextWhenIsFocused,
    hasError: tableForm.formState.errors?.priceItem,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
    amountItem,
  };
};

export type PublicOfferQuantityController = ReturnType<
  typeof usePublicOfferAssetQuantityController
>;
